import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

const ThemeModeContext = createContext(null);

const STORAGE_KEY = "esc_theme_mode";

const getSystemMode = () =>
  window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";

export function ThemeModeProvider({ children }) {
  const [override, setOverride] = useState(() => localStorage.getItem(STORAGE_KEY));
  const [systemMode, setSystemMode] = useState(getSystemMode);

  useEffect(() => {
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => setSystemMode(e.matches ? "dark" : "light");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const mode = override ?? systemMode;

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
  }, [mode]);

  const setMode = useCallback((next) => {
    setOverride(next);
    localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const toggleMode = useCallback(() => {
    setMode(mode === "dark" ? "light" : "dark");
  }, [mode, setMode]);

  const resetToSystem = useCallback(() => {
    setOverride(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const value = useMemo(
    () => ({ mode, isFollowingSystem: override === null, setMode, toggleMode, resetToSystem }),
    [mode, override, setMode, toggleMode, resetToSystem]
  );

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}

export const useThemeMode = () => useContext(ThemeModeContext);
